import { useState, useRef, useEffect } from 'react';
import RecordRTC from 'recordrtc';
import useAxiosPrivate from '../../hooks/useAxiosPrivate';
import AudioMessageService from '../../services/audioMessageService';
import ListenVoice from './ListenVoice';

const SimpleVoiceRecorderRTC = ({
  onRecordingComplete,
  onMicrophoneError,
  onNext,
  showNextButton = false,
  onRecordingStateChange,
  onUploadStateChange,
  resetCounter = 0,
}) => {
  // СОСТОЯНИЯ
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [smoothProgress, setSmoothProgress] = useState(0);
  const [micReady, setMicReady] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  // DEBUG
  const [debugLogs, setDebugLogs] = useState([]);
  const [showDebug, setShowDebug] = useState(true);

  const addDebugLog = (message, type = 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    const log = { timestamp, message, type };
    setDebugLogs((prev) => [...prev.slice(-20), log]); // Храним последние 20 логов
    console.log(`[${timestamp}] ${message}`);
  };

  // СЕРВИСЫ
  const axiosPrivate = useAxiosPrivate();
  const audioService = useRef(new AudioMessageService(axiosPrivate));

  // РЕФЫ
  const recorder = useRef(null);
  const stream = useRef(null);
  const timer = useRef(null);
  const progressTimer = useRef(null);
  const startTime = useRef(null);
  const currentBlob = useRef(null);

  // КОНСТАНТЫ
  const MAX_DURATION = 30;
  const radius = 100;
  const circumference = 2 * Math.PI * radius;

  // === ИНИЦИАЛИЗАЦИЯ МИКРОФОНА ===
  useEffect(() => {
    const initMicrophone = async () => {
      try {
        addDebugLog('🎤 Инициализируем RecordRTC', 'info');

        // Получаем поток микрофона
        const mediaStream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
            sampleRate: 44100,
          },
        });

        stream.current = mediaStream;
        setMicReady(true);
        addDebugLog('✅ Микрофон готов (RecordRTC)', 'success');
      } catch (error) {
        addDebugLog(`❌ Ошибка доступа к микрофону: ${error.message}`, 'error');
        setMicReady(false);
        onMicrophoneError?.();
      }
    };

    initMicrophone();

    return () => {
      // Cleanup
      if (timer.current) clearInterval(timer.current);
      if (progressTimer.current) clearInterval(progressTimer.current);
      if (stream.current) {
        stream.current.getTracks().forEach((track) => track.stop());
      }
      if (recorder.current) {
        try {
          recorder.current.destroy();
        } catch (e) {}
      }
      if (audioUrl && audioUrl.startsWith('blob:')) {
        URL.revokeObjectURL(audioUrl);
      }
    };
  }, []);

  useEffect(() => {
    onRecordingStateChange?.(isRecording);
  }, [isRecording, onRecordingStateChange]);

  useEffect(() => {
    onUploadStateChange?.(isUploading);
  }, [isUploading, onUploadStateChange]);

  useEffect(() => {
    if (resetCounter > 0) {
      handleReset();
    }
  }, [resetCounter]);

  useEffect(() => {
    return () => {
      if (audioUrl && audioUrl.startsWith('blob:')) {
        URL.revokeObjectURL(audioUrl);
      }
    };
  }, [audioUrl]);

  // === ПЛАВНЫЙ ПРОГРЕСС ===
  useEffect(() => {
    if (isRecording) {
      startTime.current = Date.now();
      setSmoothProgress(0);
      progressTimer.current = setInterval(() => {
        const elapsed = Date.now() - startTime.current;
        const progress = Math.min((elapsed / (MAX_DURATION * 1000)) * 100, 100);
        setSmoothProgress(progress);
      }, 100);
    } else {
      setSmoothProgress(0);
      if (progressTimer.current) {
        clearInterval(progressTimer.current);
        progressTimer.current = null;
      }
    }
    return () => {
      if (progressTimer.current) clearInterval(progressTimer.current);
    };
  }, [isRecording]);

  // === СТАРТ ЗАПИСИ ===
  const startRecording = async () => {
    addDebugLog('🔍 startRecording вызван', 'info');

    if (isRecording) {
      addDebugLog('⚠️ Уже идёт запись', 'warn');
      return;
    }

    try {
      addDebugLog('🎬 СТАРТ ЗАПИСИ (RecordRTC)', 'info');

      // Освобождаем старый blob URL
      if (audioUrl && audioUrl.startsWith('blob:')) {
        addDebugLog('♻️ Освобождаем старый blob URL', 'info');
        URL.revokeObjectURL(audioUrl);
        setAudioUrl(null);
      }

      // Чистим состояние
      addDebugLog('🧹 Чистим состояние', 'info');
      currentBlob.current = null;
      setRecordingTime(0);
      setSmoothProgress(0);
      setUploadError(null);

      // 🔧 КРИТИЧНО: Освобождаем старый поток микрофона
      if (stream.current) {
        addDebugLog('🛑 Останавливаем старый поток микрофона', 'info');
        stream.current.getTracks().forEach((track) => {
          track.stop();
          addDebugLog(`  └─ Трек ${track.kind} остановлен`, 'info');
        });
        stream.current = null;
      }

      // 🔧 КРИТИЧНО: Запрашиваем НОВЫЙ поток микрофона перед КАЖДОЙ записью
      addDebugLog('🎤 Запрашиваем НОВЫЙ поток микрофона...', 'info');
      try {
        const newStream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
            sampleRate: 44100,
          },
        });
        stream.current = newStream;
        addDebugLog(`✅ Новый поток получен, треков: ${newStream.getAudioTracks().length}`, 'success');
      } catch (err) {
        addDebugLog(`❌ Ошибка доступа к микрофону: ${err.message}`, 'error');
        onMicrophoneError?.();
        return;
      }

      // Уничтожаем старый recorder если есть
      if (recorder.current) {
        addDebugLog('🗑️ Уничтожаем старый recorder', 'info');
        try {
          recorder.current.destroy();
        } catch (e) {
          addDebugLog(`⚠️ Ошибка destroy: ${e.message}`, 'warn');
        }
        recorder.current = null;
      }

      // Создаём НОВЫЙ RecordRTC recorder с НОВЫМ потоком
      addDebugLog('🆕 Создаём новый RecordRTC с новым потоком', 'info');
      recorder.current = new RecordRTC(stream.current, {
        type: 'audio',
        mimeType: 'audio/webm',
        recorderType: RecordRTC.StereoAudioRecorder,
        desiredSampRate: 16000,
        numberOfAudioChannels: 1,
        bufferSize: 16384,
        audioBitsPerSecond: 128000,
        checkForInactiveTracks: true,
        disableLogs: false,
      });

      // Запускаем запись
      addDebugLog('▶️ Запускаем startRecording()', 'info');
      recorder.current.startRecording();
      setIsRecording(true);

      // Таймер
      timer.current = setInterval(() => {
        setRecordingTime((prev) => {
          const newTime = prev + 1;
          if (newTime >= MAX_DURATION) {
            stopRecording();
            return MAX_DURATION;
          }
          return newTime;
        });
      }, 1000);

      addDebugLog('✅ ЗАПИСЬ НАЧАЛАСЬ (RecordRTC)', 'success');
    } catch (error) {
      addDebugLog(`❌ Ошибка старта записи: ${error.message}`, 'error');
      setIsRecording(false);
      onMicrophoneError?.();
    }
  }; // === СТОП ЗАПИСИ ===
  const stopRecording = () => {
    addDebugLog('🔍 stopRecording вызван', 'info');

    if (!isRecording) {
      addDebugLog('⚠️ Запись не идёт (isRecording = false)', 'warn');
      return;
    }
    if (!recorder.current) {
      addDebugLog('❌ Нет recorder.current!', 'error');
      return;
    }

    addDebugLog('🛑 СТОП ЗАПИСИ (RecordRTC)', 'info');
    setIsRecording(false);

    if (timer.current) {
      clearInterval(timer.current);
      timer.current = null;
      addDebugLog('⏹ Таймер остановлен', 'info');
    }
    if (progressTimer.current) {
      clearInterval(progressTimer.current);
      progressTimer.current = null;
      addDebugLog('⏹ Прогресс таймер остановлен', 'info');
    }

    try {
      addDebugLog('📞 Вызываем recorder.stopRecording()', 'info');

      // RecordRTC использует callback для получения blob
      recorder.current.stopRecording(async () => {
        addDebugLog('🎵 Callback stopRecording выполнен', 'success');

        // Получаем blob
        const blob = recorder.current.getBlob();
        addDebugLog(`✅ Blob создан, размер: ${blob.size} байт`, 'success');

        if (blob.size === 0 || blob.size < 1000) {
          addDebugLog(`❌ Blob пустой или слишком маленький: ${blob.size}`, 'error');
          return;
        }

        // Сохраняем blob
        currentBlob.current = blob;
        addDebugLog('💾 Blob сохранён в currentBlob.current', 'info');

        // Создаём URL для воспроизведения
        const blobUrl = URL.createObjectURL(blob);
        addDebugLog(`✅ Blob URL создан: ${blobUrl}`, 'success');
        setAudioUrl(blobUrl);

        // 🔧 КРИТИЧНО: Полная очистка старой сессии после получения blob
        addDebugLog('🧹 Полная очистка старой сессии записи', 'info');

        if (recorder.current) {
          try {
            recorder.current.destroy();
            addDebugLog('  └─ Recorder destroyed', 'info');
          } catch (e) {
            addDebugLog(`  └─ Ошибка destroy: ${e.message}`, 'warn');
          }
          recorder.current = null;
        }

        if (stream.current) {
          stream.current.getTracks().forEach((track) => {
            track.stop();
            addDebugLog(`  └─ Трек ${track.kind} остановлен`, 'info');
          });
          stream.current = null;
        }

        addDebugLog('✅ Старая сессия полностью очищена', 'success');

        // Загружаем на сервер в фоне
        setIsUploading(true);
        setUploadError(null);
        addDebugLog('📤 Начинаем загрузку на сервер...', 'info');

        try {
          const result = await audioService.current.uploadAudio(blob, recordingTime);
          addDebugLog(`✅ Загрузка завершена! Audio ID: ${result.audioId}`, 'success');
          setIsUploading(false);

          onRecordingComplete?.({
            ...result,
            blob,
            audioUrl: blobUrl,
            size: blob.size,
            duration: recordingTime,
          });
        } catch (uploadError) {
          addDebugLog(`❌ Ошибка загрузки: ${uploadError.message}`, 'error');
          setIsUploading(false);
          setUploadError('Не удалось загрузить аудио на сервер');

          onRecordingComplete?.({
            blob,
            audioUrl: blobUrl,
            size: blob.size,
            duration: recordingTime,
          });
        }
      });
    } catch (error) {
      addDebugLog(`❌ Ошибка при остановке записи: ${error.message}`, 'error');
    }
  };

  // === СБРОС ===
  const handleReset = () => {
    addDebugLog('🔄 СБРОС ВЫЗВАН (RecordRTC)', 'info');
    addDebugLog(
      `📊 Текущее состояние: isRecording=${isRecording}, audioUrl=${!!audioUrl}, recorder=${!!recorder.current}`,
      'info',
    );

    if (isRecording) {
      addDebugLog('⚠️ Запись идёт, останавливаем перед сбросом', 'warn');
      stopRecording();
      return;
    }

    if (timer.current) {
      clearInterval(timer.current);
      timer.current = null;
      addDebugLog('⏹ Timer очищен', 'info');
    }
    if (progressTimer.current) {
      clearInterval(progressTimer.current);
      progressTimer.current = null;
      addDebugLog('⏹ ProgressTimer очищен', 'info');
    }

    // 🔧 КРИТИЧНО: Полностью уничтожаем recorder
    if (recorder.current) {
      try {
        recorder.current.destroy();
        addDebugLog('🗑️ Recorder уничтожен', 'success');
      } catch (e) {
        addDebugLog(`⚠️ Ошибка destroy recorder: ${e.message}`, 'warn');
      }
      recorder.current = null;
    }

    // 🔧 КРИТИЧНО: Полностью останавливаем поток микрофона
    if (stream.current) {
      addDebugLog('🛑 Останавливаем поток микрофона', 'info');
      stream.current.getTracks().forEach((track) => {
        track.stop();
        addDebugLog(`  └─ Трек ${track.kind} остановлен`, 'info');
      });
      stream.current = null;
      addDebugLog('✅ Поток микрофона полностью остановлен', 'success');
    }

    // Освобождаем URL
    const urlToRevoke = audioUrl;
    if (audioUrl && audioUrl.startsWith('blob:')) {
      addDebugLog(`♻️ Планируем освобождение blob URL: ${audioUrl}`, 'info');
      setTimeout(() => {
        try {
          URL.revokeObjectURL(urlToRevoke);
          addDebugLog('✅ Blob URL освобождён', 'success');
        } catch (e) {
          addDebugLog(`⚠️ Ошибка revoke URL: ${e.message}`, 'warn');
        }
      }, 1000);
    }

    currentBlob.current = null;
    addDebugLog('💾 currentBlob.current = null', 'info');

    setRecordingTime(0);
    setSmoothProgress(0);
    setAudioUrl(null);
    addDebugLog('🔄 setAudioUrl(null) вызван', 'info');

    setIsUploading(false);
    setUploadError(null);

    addDebugLog('✅ СБРОС ЗАВЕРШЕН (RecordRTC)', 'success');

    // Проверяем что состояние действительно сброшено
    setTimeout(() => {
      addDebugLog(`🔍 Проверка после сброса: audioUrl=${audioUrl}, isRecording=${isRecording}`, 'info');
    }, 100);
  };

  // === КЛИК ===
  const handleRecordClick = () => {
    addDebugLog('🖱️ handleRecordClick вызван', 'info');

    if (!micReady) {
      addDebugLog('❌ Микрофон не готов', 'error');
      onMicrophoneError?.();
      return;
    }

    if (isRecording) {
      addDebugLog('⏹️ Останавливаем запись', 'info');
      stopRecording();
    } else {
      addDebugLog('▶️ Запускаем запись', 'info');
      startRecording();
    }
  };

  const strokeDashoffset = circumference - (smoothProgress / 100) * circumference;

  return (
    <div className="flex flex-col items-center">
      {/* Кнопка записи */}
      {!audioUrl && (
        <div className="w-full h-[220px] flex items-center justify-center relative mt-10 select-none">
          <svg width="220" height="220" viewBox="0 0 220 220" fill="none">
            {/* Фоновые круги */}
            <circle opacity="0.2" cx="110" cy="110" r="100" fill="#233636" />
            <circle opacity="0.2" cx="110" cy="110" r="50" fill="#233636" />
            <circle opacity="0.2" cx="110" cy="110" r="79" fill="#233636" />

            {/* Контур */}
            <path
              d="M210 110C210 165.228 165.228 210 110 210C54.7715 210 10 165.228 10 110C10 54.7715 54.7715 10 110 10C165.228 10 210 54.7715 210 110ZM12 110C12 164.124 55.8761 208 110 208C164.124 208 208 164.124 208 110C208 55.8761 164.124 12 110 12C55.8761 12 12 55.8761 12 110Z"
              fill="#233636"
            />

            {/* Прогресс */}
            <circle
              cx="110"
              cy="110"
              r={radius}
              fill="none"
              stroke="#A1F69E"
              strokeWidth="2"
              strokeDasharray={circumference}
              strokeDashoffset={strokeDashoffset}
              transform="rotate(-90 110 110)"
              className="transition-all duration-100 ease-linear"
            />

            {/* Иконка микрофона */}
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M109.143 86C106.464 86 103.895 87.0642 102 88.9586C100.106 90.8529 99.0417 93.4222 99.0417 96.1012V107.843C99.0417 110.522 100.106 113.091 102 114.986C103.895 116.88 106.464 117.944 109.143 117.944C111.822 117.944 114.391 116.88 116.286 114.986C118.18 113.091 119.244 110.522 119.244 107.843V96.1012C119.244 93.4222 118.18 90.8529 116.286 88.9586C114.391 87.0642 111.822 86 109.143 86ZM106.571 127.169V131.326C106.571 132.746 107.723 133.897 109.143 133.897C110.563 133.897 111.714 132.746 111.714 131.326V127.169C121.257 126.721 128.857 118.842 128.857 109.188C128.857 107.768 127.706 106.617 126.286 106.617C124.866 106.617 123.714 107.768 123.714 109.188C123.714 116.289 117.958 122.046 110.857 122.046H109.19C109.174 122.045 109.159 122.045 109.143 122.045C109.127 122.045 109.112 122.045 109.096 122.046H107.429C100.328 122.046 94.5714 116.289 94.5714 109.188C94.5714 107.768 93.4202 106.617 92 106.617C90.5799 106.617 89.4286 107.768 89.4286 109.188C89.4286 118.842 97.0283 126.721 106.571 127.169Z"
              fill={!micReady ? '#FF6B6B' : '#A1F69E'}
              opacity={isRecording ? '1' : micReady ? '0.7' : '0.4'}
            />

            {/* Блокировка */}
            {!micReady && (
              <g>
                <line x1="90" y1="90" x2="130" y2="130" stroke="#FF6B6B" strokeWidth="3" strokeLinecap="round" />
                <circle cx="110" cy="110" r="25" fill="none" stroke="#FF6B6B" strokeWidth="2" strokeDasharray="5,5" />
              </g>
            )}

            {/* Индикатор прогресса */}
            <circle
              cx={110 + Math.cos(-Math.PI / 2 + (2 * Math.PI * smoothProgress) / 100) * radius}
              cy={110 + Math.sin(-Math.PI / 2 + (2 * Math.PI * smoothProgress) / 100) * radius}
              r="5"
              fill="#A1F69E"
              opacity={isRecording ? '1' : '0'}
              className="transition-all duration-100 ease-linear"
            />
          </svg>

          <button
            onClick={handleRecordClick}
            disabled={!micReady}
            className={`absolute inset-0 w-full h-full ${
              micReady ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'
            }`}
          />
        </div>
      )}

      {/* Время записи */}
      {!audioUrl && (
        <p className="mt-4 text-white text-center">
          {isRecording ? (
            <p className="uppercase">Идёт запись...</p>
          ) : (
            <p className="uppercase max-w-[250px]">Нажмите и удерживайте для записи</p>
          )}
        </p>
      )}

      {/* Плеер */}
      {audioUrl && (
        <>
          <div className="mt-6 w-[343px]">
            <ListenVoice preSignedAudio={audioUrl} />
          </div>

          {/* Ошибка загрузки */}
          {uploadError && <div className="mt-2 text-red-400 text-xs text-center">{uploadError}</div>}

          {/* Кнопка "Далее" */}
          {showNextButton && onNext && (
            <div className="mt-6">
              <button
                onClick={onNext}
                disabled={isUploading}
                className={`w-[343px] h-[56px] rounded-[8px] font-semibold text-[18px] transition-all ${
                  isUploading
                    ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                    : 'bg-[#A1F69E] text-black hover:bg-[#90e58d]'
                }`}
              >
                {isUploading ? 'Загрузка...' : 'Далее'}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SimpleVoiceRecorderRTC;
